import Modal from "react-responsive-modal";
import { useDarkMode } from "@/hooks/useDarkMode";
import Loader from "@/components/common/Loader";
import {
  useI18N,
  usePostFlagNote,
  useZoomDetection,
  useModalHeight,
} from "common/hooks";
import { Textarea } from "@/shadcn/components/ui/textarea";
import { useEffect, useRef, useState } from "react";
import { Input } from "@/shadcn/components/ui/input";
import { XIcon } from "lucide-react";
import { readFile } from "@/helpers/cropImage";
import { AddPhotoImageData, businessNotesAll } from "common/types";
import Zoom from "react-medium-image-zoom";
import { createPageAnalytics } from "common/analytics";
import { PhotoGuardrailError } from "common/apis";
import { useCustomToast } from "@/hooks/useCustomToast";
import { useGuardrailImageFeedback } from "@/hooks/useGuardrailImageFeedback";
import WideButton from "@/components/common/WideButton";
import { cn } from "@/lib/utils";
import { StandardButton } from "@/components/common/StandardButton";

type FlagVenueNoteProps = {
  note?: businessNotesAll;
  onClose: () => void;
  venueDetails: any;
};

function FlagVenueNote({ note, onClose, venueDetails }: FlagVenueNoteProps) {
  const isDark = useDarkMode();
  const i18n = useI18N();
  const { modalStyle, modalClassName } = useModalHeight();

  const showToast = useCustomToast();

  const [noteText, setNoteText] = useState("");
  const noteRef = useRef<HTMLTextAreaElement>(null);
  const [attachedImages, setAttachedImages] = useState<AddPhotoImageData[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>(null);
  const isZoomedIn = useZoomDetection();
  const { data, error, mutate, status } = usePostFlagNote(true);

  const guardrail = useGuardrailImageFeedback(data, error, {
    savedLabel: i18n.get("photoGuardrailReportSaved"),
  });

  // flag_alert_form page (open/close/alert_flagged). venueDetails here is the venue-sheet object,
  // so business_id = business.id (the legacy calls below read venueDetails.id, which is inert).
  const flagFormA = createPageAnalytics("flag_alert_form");
  const flagFormProps = () => ({
    alert_title: note?.action_title ?? "",
    brand_id: venueDetails?.brandId != null ? String(venueDetails?.brandId) : "",
    brand_name: venueDetails?.brandName ?? "",
    business_id:
      venueDetails?.business?.id != null
        ? String(venueDetails.business.id)
        : "",
    place_name: venueDetails?.name ?? "",
  });

  // flag_alert_form_opened — mount, ref-guarded against StrictMode's double mount-effect.
  const openedFiredRef = useRef(false);
  useEffect(() => {
    if (openedFiredRef.current) return;
    openedFiredRef.current = true;
    flagFormA.track("page_view_page", flagFormProps());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // flag_alert_form_closed — once, on explicit dismiss (X / backdrop / Escape). The post-submit
  // close goes through the success effect's onClose(), which does NOT call this (alert_flagged
  // fires instead).
  const closedFiredRef = useRef(false);
  const fireFlagFormClosed = () => {
    if (closedFiredRef.current) return;
    closedFiredRef.current = true;
    flagFormA.track("close_action_button", flagFormProps());
  };

  useEffect(() => {
    if (!error) return;
    if (error instanceof PhotoGuardrailError) {
      if (guardrail.rejectedInputIndexes.length) {
        setAttachedImages((prev) =>
          prev.filter((_, i) => !guardrail.rejectedInputIndexes.includes(i)),
        );
      }
      return;
    }
    showToast(error.message, "error");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, isDark]);

  useEffect(() => {
    if (data) {
      if (!guardrail.isPartial) {
        showToast(data.message, "success");
      }
      setAttachedImages([]);
      setNoteText("");
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isDark]);

  const onFileChange = async (e) => {
    const newImages = Array.from(e.target.files).map(async (file) => {
      const imageDataUrl = await readFile(file);
      return {
        imageUrl: imageDataUrl,
        note: "",
        selectedOption: undefined,
        key: undefined,
      };
    });
    const imgs = await Promise.all(newImages);
    setAttachedImages((prev) => [...prev, ...imgs]);
  };

  const submit = () => {
    if (noteText.length < 5) {
      noteRef.current?.focus();
      setErrorMessage("Note must be at least 5 characters long");
      return;
    }
    if (attachedImages.length === 0) {
      setErrorMessage("Please add at least one image");
      return;
    }
    setErrorMessage(null);

    // submit_flag_action_button -> alert_flagged. photos_count = images attached to the flag
    // (the validation above guarantees at least one).
    flagFormA.track("submit_flag_action_button", {
      ...flagFormProps(),
      photos_count: attachedImages.length,
    });

    console.log("submit", noteText, attachedImages);

    mutate({
      noteId: note.business_note_text_id.toString(),
      text: noteText,
      photos: attachedImages,
    });
  };

  const firstFocusRef = useRef(null); // Reference for the element we need to set focus

  // Focus on the element when the component mounts
  useEffect(() => {
    if (firstFocusRef.current) {
      firstFocusRef.current.focus();
    }
  }, []);

  return (
    <Modal
      focusTrapped
      center
      blockScroll={true}
      animationDuration={0}
      open={true}
      onClose={() => {
        fireFlagFormClosed();
        onClose();
      }}
      classNames={{
        modal: "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
        closeButton: "hidden",
      }}
    >
      {status === "pending" && <Loader />}
      <section
        style={modalStyle}
        className={cn(modalClassName)}
        role="none"
        aria-label={i18n.get("flagAccessibility")}
        ref={firstFocusRef}
      >
        <header
          className={`py-4 min-h-14 flex items-center justify-between text-center px-6 border-b border-border-token-brand-minimal bg-bg-brand-secondary-default ${!isZoomedIn ? "sticky top-0 z-10" : "relative z-10"} relative`}
        >
          <StandardButton
            onClick={() => {
              fireFlagFormClosed();
              setAttachedImages([]);
              setNoteText("");
              onClose();
            }}
            variant="closeModal"
            icon={<XIcon color={isDark ? "#A39FE8" : "#352FC3"} />}
            aria-label="Close modal"
            tabIndex={0}
            className="flex-shrink-0"
          />
          <div className="title-s-semibold text-center text-fg-neutral-strong flex-1 px-4">
            {i18n.get("flagVenueNote")}
          </div>
          <div className="flex-shrink-0 w-10"></div>{" "}
          {/* Spacer to balance the layout */}
        </header>

        <div
          aria-label="Modal Content"
          role="none"
          className="flex-grow overflow-y-auto py-3 px-3"
        >
          <div className="self-stretch px-6 pt-2 flex-col justify-start items-center gap-2 flex">
            <div className="self-stretch h-30 pt-2 opacity-80 flex-col justify-start items-start gap-1 flex">
              <div className="self-stretch h-12 flex-col justify-start items-start gap-2 flex">
                <div className="self-stretch !text-fg-brand-subtle label-s-medium">
                  {venueDetails.name}
                </div>
                <h1 className="self-stretch text-fg-neutral-strong title-s-semibold">
                  {i18n.get("helpUsCatchEveryError")}
                </h1>
              </div>
              <div className="self-stretch text-fg-neutral-strong body-l-semibold md:title-s-semibold pt-1">
                {i18n.get("flagVenueNoteMessage")}
              </div>

              <div className="border w-full border-border-token-brand-minimal mt-2 h-[1px]"></div>
            </div>

            <div className="self-stretch md:px-1 rounded-lg justify-start items-center gap-4 inline-flex">
              <div className="w-[24px] h-[24px] relative">
                <img
                  src={isDark ? "/images/Info_dark.svg" : "/images/Info.svg"}
                  alt=""
                  aria-hidden="true"
                  className="w-6 h-6 self-start cursor-pointer"
                />
              </div>
              <div className="h-14 flex-col justify-center items-start gap-1 inline-flex">
                <div className="w-full title-s-semibold text-fg-neutral-strong">
                  {i18n.get("venueNote")}
                </div>
                <div className="label-m-medium">{note.action_title}</div>
              </div>
            </div>

            <div className="border w-full border-border-token-brand-minimal mb-2 h-[1px]"></div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="w-full">
                <label htmlFor="review" className="sr-only">
                  {i18n.get("tellUsWhatIsIncorrect")}
                </label>
                <Textarea
                  id="review"
                  placeholder={i18n.get("tellUsWhatIsIncorrect")}
                  className="scrollbar-thin h-28"
                  value={noteText}
                  aria-describedby="charCount"
                  onChange={(e) => {
                    setErrorMessage(null);
                    setNoteText(e.target.value);
                  }}
                  maxLength={150}
                  ref={noteRef}
                  error={(noteText.length > 0 && noteText.length < 5) || Boolean(errorMessage)}
                />
                <div
                  id="charCount"
                  className={`label-s-medium mt-3 ${
                    noteText.length > 0 && noteText.length < 5
                      ? "text-fg-error-subtle"
                      : "text-fg-neutral-subtle"
                  }`}
                >
                  {noteText.length > 0 && noteText.length < 5
                    ? i18n.get("pleaseEnterAtLeast5Characters")
                    : `${noteText.length}/150 characters`}
                </div>

                {errorMessage && !(noteText.length > 0 && noteText.length < 5) && (
                  <div className="text-fg-error-subtle" role="alert">
                    {errorMessage}
                  </div>
                )}
              </div>
            </div>

            <div className="w-full flex-col justify-center items-start gap-1 flex">
              <div className="grid gap-4 w-full">
                {attachedImages.map((image, index) => (
                  <div key={index} className="grid gap-2">
                    <div className="relative w-full h-auto" tabIndex={0}>
                      <Zoom>
                        <img
                          src={image.imageUrl}
                          alt={`Attached image ${index + 1}`}
                          className="w-full h-[300px] object-cover rounded-[10px]"
                        />
                      </Zoom>
                      <StandardButton
                        onClick={() => {
                          setAttachedImages(
                            attachedImages.filter(
                              (_, imgIndex) => imgIndex !== index,
                            ),
                          );
                        }}
                        variant="removeImage"
                        position="absolute"
                        className="top-2 right-3 m-2"
                        imageSrc="/images/Close.svg"
                        imageAlt="Remove image"
                        imageContainer={true}
                        imageContainerClassName="w-6 h-6 p-1 bg-[#f9f8fd] rounded border border-[#cbc9f2] justify-center items-center inline-flex"
                        aria-label={`Remove attached image ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <footer
          className={`py-3 min-h-14 flex items-center justify-center text-center px-6 bg-bg-brand-secondary-default z-1000 ${!isZoomedIn ? "sticky bottom-0" : "relative"}`}
        >
          <div className="flex-col justify-end items-center gap-4 flex md:w-full">
            <div className="w-full justify-start items-center gap-4 inline-flex">
              <label
                htmlFor="picture"
                className="grow shrink basis-0 h-12 px-6 py-3 bg-container-brand-minimal rounded-xl border border-[#cbc9f2] justify-center items-center gap-3 flex cursor-pointer"
                aria-label="Add photos"
              >
                <div className="text-center text-fg-neutral-strong label-l-bold w-full">
                  {i18n.get("addPhotos")}
                </div>
                <Input
                  id="picture"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFileChange}
                />
              </label>
            </div>

            <div className="w-full justify-center items-center inline-flex">
              <WideButton
                className="grow"
                disabled={noteText.length < 5}
                onClick={() => {
                  submit();
                }}
                aria-label="Submit flag"
              >
                {i18n.get("submitFlag")}
              </WideButton>
            </div>
          </div>
        </footer>
      </section>
    </Modal>
  );
}

export default FlagVenueNote;
